import { Stethoscope, CalendarCheck, ClipboardList } from 'lucide-react';
import DigitalSignatureBlock from '../components/documents/DigitalSignatureBlock';

interface Props {
  data: {
    diagnosis: string;
    complaints?: string;
    medicines: {
      name: string;
      dosage: string;
      frequency: string;
      duration: string;
      route: string;
      instructions?: string;
    }[];
    advice?: string[];
    followUpDate?: string;
    doctorName: string;
    doctorNhcId?: string;
    doctorLicense?: string;
    speciality?: string;
    signatories: {
      name: string;
      role: string;
      nhcId?: string;
      licenseNo?: string;
      institution?: string;
    }[];
  };
}

export default function PrescriptionTemplate({ data }: Props) {
  return (
    <div className="space-y-3">
      {/* Rx Header */}
      <div className="flex items-start justify-between">
        <div>
          <span className="text-3xl font-bold font-serif text-gray-800 leading-none">&#8478;</span>
        </div>
        <div className="text-right text-[10px] text-gray-600">
          <p className="font-semibold text-gray-800">{data.doctorName}</p>
          {data.speciality && <p>{data.speciality}</p>}
          {data.doctorNhcId && <p className="font-mono">{data.doctorNhcId}</p>}
          {data.doctorLicense && <p>Lic: {data.doctorLicense}</p>}
        </div>
      </div>

      <div className="border-t border-gray-300 pt-3 space-y-3">
        {/* Complaints */}
        {data.complaints && (
          <div>
            <div className="flex items-center gap-1.5 mb-1">
              <Stethoscope className="w-3 h-3 text-gray-600" />
              <h3 className="text-[10px] font-bold uppercase tracking-wider text-gray-600">
                Chief Complaints
              </h3>
            </div>
            <p className="text-[11px] text-gray-800 pl-1">{data.complaints}</p>
          </div>
        )}

        {/* Diagnosis */}
        <div>
          <h3 className="text-[10px] font-bold uppercase tracking-wider text-gray-600 mb-1">
            Diagnosis
          </h3>
          <p className="text-[11px] text-gray-800 font-medium pl-1">{data.diagnosis}</p>
        </div>
      </div>

      {/* Medicines Table */}
      <div>
        <div className="flex items-center gap-1.5 mb-2">
          <ClipboardList className="w-3 h-3 text-gray-600" />
          <h3 className="text-[10px] font-bold uppercase tracking-wider text-gray-600">
            Prescribed Medications
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[10px]">
            <thead>
              <tr className="bg-gray-100">
                <th className="text-left font-bold text-gray-700 py-1 px-1.5 border border-gray-300">
                  #
                </th>
                <th className="text-left font-bold text-gray-700 py-1 px-1.5 border border-gray-300">
                  Medicine
                </th>
                <th className="text-left font-bold text-gray-700 py-1 px-1.5 border border-gray-300">
                  Dosage
                </th>
                <th className="text-left font-bold text-gray-700 py-1 px-1.5 border border-gray-300">
                  Frequency
                </th>
                <th className="text-left font-bold text-gray-700 py-1 px-1.5 border border-gray-300">
                  Duration
                </th>
                <th className="text-left font-bold text-gray-700 py-1 px-1.5 border border-gray-300">
                  Route
                </th>
                <th className="text-left font-bold text-gray-700 py-1 px-1.5 border border-gray-300">
                  Instructions
                </th>
              </tr>
            </thead>
            <tbody>
              {data.medicines.map((med, i) => (
                <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="py-1 px-1.5 border border-gray-200 text-gray-500 text-center">
                    {i + 1}
                  </td>
                  <td className="py-1 px-1.5 border border-gray-200 font-medium text-gray-800">
                    {med.name}
                  </td>
                  <td className="py-1 px-1.5 border border-gray-200 text-gray-700">{med.dosage}</td>
                  <td className="py-1 px-1.5 border border-gray-200 text-gray-700">
                    {med.frequency}
                  </td>
                  <td className="py-1 px-1.5 border border-gray-200 text-gray-700">
                    {med.duration}
                  </td>
                  <td className="py-1 px-1.5 border border-gray-200 text-gray-700">{med.route}</td>
                  <td className="py-1 px-1.5 border border-gray-200 text-gray-600 italic">
                    {med.instructions || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Advice */}
      {data.advice && data.advice.length > 0 && (
        <div>
          <h3 className="text-[10px] font-bold uppercase tracking-wider text-gray-600 mb-1">
            Advice
          </h3>
          <ul className="space-y-0.5 pl-1">
            {data.advice.map((item, i) => (
              <li key={i} className="text-[10px] text-gray-700 flex items-start gap-1.5">
                <span className="text-gray-400 mt-0.5">&#8226;</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Follow-Up */}
      {data.followUpDate && (
        <div className="flex items-center gap-1.5 text-[10px] text-gray-700 bg-gray-50 px-3 py-2 border border-gray-200 rounded">
          <CalendarCheck className="w-3 h-3 text-gray-600" />
          <span className="font-semibold">Follow-Up Date:</span>
          <span>{data.followUpDate}</span>
        </div>
      )}

      {/* Digital Signatures */}
      <DigitalSignatureBlock signatories={data.signatories} />
    </div>
  );
}
